/**
 * Home — launchpad page shown at `/`.
 *
 * Renders permission-gated tiles that mirror the sidebar. Each section
 * matches a sidebar category; admin items live in their own row at the
 * bottom for users with the relevant permissions.
 *
 * The data comes from the SAME arrays the sidebar uses (exported from
 * components/Layout.jsx) so adding a new sidebar item shows up here
 * automatically — no duplication.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion, REGIONS } from "@/contexts/RegionContext";
import { Flag } from "@/components/Flag";
import { Home as HomeIcon, ChevronRight } from "lucide-react";
import { TypewriterGreeting } from "@/components/TypewriterGreeting";
import { isHalloweenSeason, HalloweenPumpkin, HalloweenCobweb, HalloweenSpider } from "@/components/HalloweenDecor";
import {
  ukNavigationCategories,
  ausNavigationCategories,
  adminNavItems,
} from "@/components/Layout";

// Friendly 1-line descriptions per path. Kept here (not on the nav items
// themselves) so the sidebar stays compact and Home stays informative.
const DESCRIPTIONS = {
  "/applications":              "Live Monday pipeline — every application by stage",
  "/add-application":           "Onboard a new applicant and kick off the workflow",
  "/applications/open-banking": "Initiate Open Banking bank-statement collection",
  "/credit-deals":              "Deals approved by Credit and awaiting funding action",
  "/sales-analytics":           "Planning, forecasting, agent performance",
  "/approvals":                 "Items waiting on your sign-off",
  "/collections/daily-report":  "Today's snapshot of collections workload",
  "/collections/ageing":        "Loan arrears ageing buckets and unified case files",
  "/collections/analysis":      "Trends and effectiveness across the book",
  "/collections/bad-debt":      "Cases in / approaching bad-debt status",
  "/legal/documents":           "Onboarding Centre — contracts, e-sign, mandates",
  "/legal/case-tracker":        "Legal cases under management",
  "/legal/performance":         "Legal performance analytics",
  "/legal/credit-policy":       "Credit policy intelligence + compliance",
  "/aus/legal/documents":       "AUS Onboarding Centre — DocuPilot, DocuSign, GoCardless BECS",
  "/aus/legal/notices":         "Default Notice & LBA management for AUS",
  "/audit-trail":               "Full audit log of who changed what, when",
  "/admin/users":               "Invite, edit, manage users + role permissions",
  "/settings":                  "API credentials, SMS templates, Xero, system options",
};

const CATEGORY_ACCENTS = {
  applications: { ring: "ring-emerald-100", iconBg: "bg-emerald-50 text-emerald-700" },
  sales:        { ring: "ring-amber-100",   iconBg: "bg-amber-50 text-amber-700" },
  collections:  { ring: "ring-rose-100",    iconBg: "bg-rose-50 text-rose-700" },
  legal:        { ring: "ring-indigo-100",  iconBg: "bg-indigo-50 text-indigo-700" },
};

function Tile({ item, navigate, accent }) {
  const Icon = item.icon;
  const description = DESCRIPTIONS[item.path] || "";
  const isUC = !!item.underConstruction;
  return (
    <button
      onClick={() => !isUC && navigate(item.path)}
      disabled={isUC}
      className={`group relative text-left rounded-xl border border-slate-200 bg-white p-5 transition-all ring-1 ring-transparent hover:${accent.ring} hover:shadow-md hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none`}
      data-testid={`tile-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${accent.iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 truncate">{item.label}</h3>
            {isUC && <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Soon</span>}
          </div>
          {description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{description}</p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all shrink-0 mt-3" />
      </div>
    </button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user, hasPermission, hasAnyPermission, isAdmin } = useAuth();
  const { currentRegion, regionConfig, switchRegion } = useRegion();

  // Apply the same permission filtering the sidebar uses
  const canSee = (item) => {
    if (!item) return false;
    if (item.adminOnly && !isAdmin()) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.anyPermission && !hasAnyPermission(item.anyPermission)) return false;
    return true;
  };

  const rawCategories = currentRegion === "AUS" ? ausNavigationCategories : ukNavigationCategories;
  const categories = useMemo(
    () => rawCategories
      .map(cat => ({ ...cat, items: cat.items.filter(canSee) }))
      .filter(cat => cat.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawCategories, user]
  );

  const visibleAdmin = useMemo(() => adminNavItems.filter(canSee),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long",
  });
  const firstName = (user?.name || user?.email || "").split(/[ @]/)[0] || "there";
  const halloween = isHalloweenSeason();

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-10" data-testid="home-page">
      {/* Welcome banner */}
      <header className={`relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 ${halloween ? "hw-banner" : ""}`} data-testid="home-welcome-banner">
        {halloween && <HalloweenCobweb />}
        <div className="relative">
          {halloween && <HalloweenSpider />}
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <HomeIcon className="w-4 h-4" />
            <span>Home</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {halloween && <HalloweenPumpkin />}
            <TypewriterGreeting
              greetings={[
                { prefix: "Welcome back", name: firstName },
                { prefix: "G'day",        name: firstName },
                { prefix: "Kia ora",      name: firstName },
              ]}
              className="text-3xl sm:text-4xl font-bold text-slate-900"
            />
          </div>
          <p className="text-slate-500 mt-1">{today}</p>
        </div>
        <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 self-start sm:self-end">
          <Flag code={regionConfig?.flagCode} size={18} />
          <span className="font-medium">{regionConfig?.name} portal</span>
        </div>
      </header>

      {/* Region switcher row — quick access to other regions from Home.
          Disabled regions (e.g. New Zealand pre-launch) appear here but
          can't be selected. */}
      <div className="flex flex-wrap items-center gap-2 -mt-4" data-testid="home-region-row">
        <span className="text-xs uppercase tracking-wider text-slate-400 mr-2">Regions:</span>
        {Object.entries(REGIONS).map(([code, region]) => {
          const isActive = code === currentRegion;
          const isDisabled = !!region.comingSoon;
          return (
            <button
              key={code}
              onClick={() => {
                if (isDisabled || isActive) return;
                switchRegion(code);
              }}
              disabled={isDisabled || isActive}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-all ${
                isActive
                  ? "border-primary bg-primary/10 text-primary cursor-default"
                  : isDisabled
                    ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              }`}
              data-testid={`home-region-${code.toLowerCase()}`}
            >
              <Flag code={region.flagCode} size={14} />
              <span>{region.name}</span>
              {isDisabled && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                  Soon
                </span>
              )}
              {isActive && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-primary">Active</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Category sections */}
      {categories.map((cat) => {
        const CatIcon = cat.icon;
        const accent = CATEGORY_ACCENTS[cat.id] || CATEGORY_ACCENTS.applications;
        return (
          <section key={cat.id} data-testid={`section-${cat.id}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent.iconBg}`}>
                <CatIcon className="w-4 h-4" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800">{cat.label}</h2>
              <div className="flex-1 border-t border-slate-100 ml-3" />
              <span className="text-xs text-slate-400">{cat.items.length} {cat.items.length === 1 ? "page" : "pages"}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cat.items.map(item => (
                <Tile key={item.path} item={item} navigate={navigate} accent={accent} />
              ))}
            </div>
          </section>
        );
      })}

      {/* Admin section — only if any admin tile is visible */}
      {visibleAdmin.length > 0 && (
        <section data-testid="section-admin">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-slate-100 text-slate-700">
              <HomeIcon className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Admin</h2>
            <div className="flex-1 border-t border-slate-100 ml-3" />
            <span className="text-xs text-slate-400">{visibleAdmin.length} {visibleAdmin.length === 1 ? "page" : "pages"}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleAdmin.map(item => (
              <Tile
                key={item.path}
                item={item}
                navigate={navigate}
                accent={{ ring: "ring-slate-200", iconBg: "bg-slate-100 text-slate-700" }}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
